import { Ioc } from "@core/ioc/ioc";
import type { IStorageModel } from "@models/storage/IStorageModel";
import { STORAGE_MODEL } from "@models/storage/IStorageModel";

/**
 * `materialTextureCache.ts` — module-scoped cache of customer material
 * textures, the `blob:` URL behind every `MaterialData.Textures` entry.
 *
 * Port of the texture leg of `ProjectModel_LoadAsset.ModifyAssetUsingPartDataAsync`
 * (`ProjectModel_LoadAsset.cs:104-142`): for every `{propertyName -> textureID}`
 * pair on every material of the asset, Unity checks its on-disk cache
 * (`GetCachePathForTexture`) and downloads `textures/{textureID}` when it is
 * missing, keeping ONE `Texture2D` per texture id in `idToTexuture` so the same
 * image is shared across every material that references it. A browser has no
 * cache directory, so the S3 object is fetched once per session and held as a
 * `blob:` URL instead — the same shape `thumbnailCache` and `profilePicCache`
 * already use, including their most important property: **a cached URL
 * deliberately outlives consumer scope.** Materials are rebuilt on every
 * inspector edit and on every peer `update_material`, and revoking on teardown
 * would make each of those re-fetch S3.
 *
 * Key is `customerId|textureId`, matching the sibling caches — the object lives
 * under the OWNING account's prefix, and a cross-account shared project resolves
 * through a different customer than the viewer.
 *
 * **A negative result is cached too.** A material can reference a texture whose
 * S3 object was never uploaded (Unity's own import/asset-delete paths tolerate
 * orphan rows), and without this every material re-build would re-issue a doomed
 * GET per missing slot.
 */

const cache = new Map<string, string | null>();
const inflight = new Map<string, Promise<string | null>>();

/**
 * Resolve one `MaterialData.Textures` value to a `blob:` URL, or `null` when the
 * object is missing. `getTextureBlobUrl` already swallows 404/403 into `null`
 * (S3 answers a missing key with AccessDenied when the caller lacks
 * `s3:ListBucket`), so a `null` here means "no such texture", not "failed".
 */
export async function loadMaterialTexture(
  customerId: string,
  textureId: string,
  signal?: AbortSignal,
): Promise<string | null> {
  if (!customerId || !textureId) return null;

  const key = `${customerId}|${textureId}`;
  if (cache.has(key)) return cache.get(key) ?? null;

  const existing = inflight.get(key);
  if (existing) return existing;

  const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
  const load = storage
    .getTextureBlobUrl(customerId, textureId, signal)
    .then((url) => {
      cache.set(key, url);
      return url;
    })
    .catch((err: unknown) => {
      if (signal?.aborted) return null;
      console.warn(`[materialTextureCache] texture ${textureId} failed:`, err);
      // NOT cached — a transient failure must stay retryable, unlike a genuine
      // miss (which `getTextureBlobUrl` reports as a resolved `null`).
      return null;
    })
    .finally(() => {
      inflight.delete(key);
    });

  inflight.set(key, load);
  return load;
}

/**
 * Seed a texture this client is about to upload, so a material built before
 * (or without) the S3 PUT completing renders from the local bytes. Texture
 * objects are write-once, so the bytes cannot go stale.
 */
export function primeMaterialTexture(customerId: string, textureId: string, blob: Blob): void {
  if (!customerId || !textureId) return;
  const key = `${customerId}|${textureId}`;
  const previous = cache.get(key);
  if (previous) URL.revokeObjectURL(previous);
  cache.set(key, URL.createObjectURL(blob));
}

/** @returns how many cached URLs were dropped (for the app-settings readout). */
export function clearMaterialTextureCache(): number {
  let count = 0;
  for (const url of cache.values()) {
    if (!url) continue;
    URL.revokeObjectURL(url);
    count += 1;
  }
  cache.clear();
  inflight.clear();
  return count;
}
